// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DeviceCardMenu from "../DeviceCardMenu/DeviceCardMenu";
import {
  HeartbeatIcon,
  VROutlineIcon,
  BatteryEmptyIcon,
  BatteryFullIcon,
  BatteryHighIcon,
  BatteryLowIcon,
  BatteryMidIcon,
} from "src/assets/icons";

// Types
import { DeviceType } from "src/services/devices/types";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

type DeviceCardHeaderType = {
  device: DeviceType;
  shouldDisableLiveUpdate: boolean;
  isLinked: boolean;
  isOutdated: boolean;
};

const DeviceCardHeader = ({
  device,
  shouldDisableLiveUpdate,
  isLinked,
  isOutdated,
}: DeviceCardHeaderType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  const { actions } = useLiveUpdateContext();
  const { handleViewLiveUpdateClick } = actions;

  const isDark = theme.palette.mode === "dark";
  const offlineColor = isDark ? "#8B94A1" : "#5B6D7A70";
  const mutedTextColor = isDark ? offlineColor : "#5B6D7A";

  // Extract version number with regular expression
  const deviceVersion = useMemo(() => {
    const regex = /\d+(\.\d+)*/g;
    return device?.applicationVersion
      ? `v${device.applicationVersion.match(regex)}`
      : null;
  }, [device?.applicationVersion]);

  // Move this logic to a helper file if reused in multiple places in the future
  const batteryIcon = useMemo(() => {
    const batteryStatus = device?.deviceStatus?.battery;

    if (!batteryStatus) {
      return <Box />;
    }
    if (batteryStatus >= 0.8 && batteryStatus <= 1) {
      return (
        <Box sx={{ color: "#5B6D7A", alt: "battery full icon" }}>
          <BatteryFullIcon />
        </Box>
      );
    } else if (batteryStatus >= 0.6 && batteryStatus <= 0.79) {
      return (
        <Box sx={{ color: "#5B6D7A", alt: "battery high icon" }}>
          <BatteryHighIcon />
        </Box>
      );
    } else if (batteryStatus >= 0.4 && batteryStatus <= 0.59) {
      return (
        <Box sx={{ color: "#E2772E", alt: "battery mid icon" }}>
          <BatteryMidIcon />
        </Box>
      );
    } else if (batteryStatus >= 0.2 && batteryStatus <= 0.39) {
      return (
        <Box sx={{ color: "#E2772E", alt: "battery low icon" }}>
          <BatteryLowIcon />
        </Box>
      );
    } else if (batteryStatus >= 0 && batteryStatus <= 0.19) {
      return (
        <Box sx={{ color: "#B21515", alt: "battery empty icon" }}>
          <BatteryEmptyIcon />
        </Box>
      );
    }
  }, [device?.deviceStatus?.battery]);

  const isOnline = useMemo(
    () => device?.deviceStatus?.stage?.stageType != null,
    [device?.deviceStatus?.stage?.stageType]
  );

  return (
    <Grid
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      gap="8px"
    >
      <Grid
        display="flex"
        alignItems="center"
        gap="8px"
        sx={{ flex: 1, minWidth: 0 }}
      >
        <VROutlineIcon width="30px" height="30px" style={{ flexShrink: 0 }} />
        <Grid display="flex" flexDirection="column" sx={{ minWidth: 0 }}>
          <Tooltip title={device?.alias}>
            <Typography
              noWrap
              fontSize="14px"
              color={onSurface}
              fontWeight={600}
            >
              {device?.alias}
            </Typography>
          </Tooltip>

          {/* Version Number and Battery Icon */}
          <Grid
            display="flex"
            alignItems="center"
            gap="8px"
            sx={{ height: "12px", lineHeight: "12px" }}
          >
            <Typography fontSize="12px" color={mutedTextColor}>
              {deviceVersion}
            </Typography>
            {isOnline &&
              device?.deviceStatus?.battery &&
              device?.deviceStatus?.battery >= 0 && (
                <Box display="flex" alignItems="center" gap="4px">
                  {batteryIcon}
                  <Typography fontSize="12px" color={onSurface}>
                    {Math.floor(device?.deviceStatus?.battery * 100)}%
                  </Typography>
                </Box>
              )}
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: "8px", flexShrink: 0 }}>
        {/* Live Update Icon */}
        <Box display={isOutdated || !isLinked ? "none" : "flex"}>
          <Tooltip title={t("Live Update Panel")}>
            <span>
              <IconButton
                disableRipple
                disableFocusRipple
                disableTouchRipple
                sx={{
                  padding: "0px",
                  color: onSurface,
                  "&.Mui-disabled": {
                    color: "#5B6D7A",
                    opacity: "0.43",
                  },
                  "@keyframes color-change-icon": {
                    "0%": {
                      color: onSurface,
                    },
                    "50%": {
                      color: "#22BE39",
                    },
                    "100%": {
                      color: onSurface,
                    },
                  },
                  animation: shouldDisableLiveUpdate
                    ? "none"
                    : "color-change-icon 2s infinite",
                }}
                disabled={shouldDisableLiveUpdate}
                onClick={() => {
                  if (shouldDisableLiveUpdate) return;
                  handleViewLiveUpdateClick(device.id);
                }}
              >
                <HeartbeatIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <DeviceCardMenu device={device} isLinked={isLinked} />
      </Box>
    </Grid>
  );
};

export default DeviceCardHeader;
